/**
 * Edit form for a specialty.
 */
import * as React from 'react';
import { FileText, GraduationCap, ListOrdered } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Input } from '@/components/forms/Input';
import { Checkbox } from '@/components/forms/Checkbox';
import {
  getSpecialty,
  updateSpecialty,
  getHighSchoolLevels,
  getSpecialties,
  reorderSpecialties,
} from '@/services/specialties.service';
import type { UpdateSpecialtyDto } from '@/types/specialty.types';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { SpecialtySortableList } from './SpecialtySortableList';
import { SPECIALTY_FORM_QUERY_KEYS } from './specialty-configs';
import {
  buildUpdateSpecialtyDto,
  formatSpecialtyLevelLabel,
  isSpecialtyFormValid,
  specialtyToFormData,
  addSpecialtyLevelId,
  removeSpecialtyLevelId,
  validateSpecialtyForm,
} from './specialty-form-utils';

/** Props for {@link EditSpecialtyView}. */
interface EditSpecialtyViewProps {
  specialtyId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

/** Edit form for a specialty. */
export const EditSpecialtyView: React.FC<EditSpecialtyViewProps> = ({
  specialtyId,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = React.useState<UpdateSpecialtyDto>({});
  const [levelIds, setLevelIds] = React.useState<string[]>([]);
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [orderedSpecialtyIds, setOrderedSpecialtyIds] = React.useState<string[] | null>(null);

  const {
    data: specialty,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: SPECIALTY_FORM_QUERY_KEYS.one(specialtyId),
    queryFn: () => getSpecialty(specialtyId),
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError,
    error,
    catalogKey: 'specialtyNotFound',
    onClose,
    entityId: specialtyId,
  });

  const { data: highSchoolLevels = [] } = useQuery({
    queryKey: SPECIALTY_FORM_QUERY_KEYS.highSchoolLevels,
    queryFn: getHighSchoolLevels,
  });

  const { data: allSpecialties = [] } = useQuery({
    queryKey: SPECIALTY_FORM_QUERY_KEYS.all,
    queryFn: () => getSpecialties(false),
  });

  /** Hydrate form + linked levels once GET specialty resolves. */
  React.useEffect(() => {
    if (specialty) {
      const mapped = specialtyToFormData(specialty);
      setFormData(mapped.formData);
      setLevelIds(mapped.levelIds);
    }
  }, [specialty]);

  const updateMutation = useMutation({
    mutationFn: (dto: UpdateSpecialtyDto) => updateSpecialty(specialtyId, dto),
    onSuccess: () => {
      toast.success('Filière modifiée avec succès !');
      onSuccess?.();
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  /** Patch the label and re-validate required fields. */
  const handleLabelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = { ...formData, label: event.target.value };
    setFormData(next);
    setErrors(validateSpecialtyForm(next));
  };

  /** Patch the code and re-validate required fields. */
  const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = { ...formData, code: event.target.value };
    setFormData(next);
    setErrors(validateSpecialtyForm(next));
  };

  /** Add or drop a linked level id. */
  const handleToggleLevel = (levelId: string, checked: boolean) => {
    setLevelIds((prev) =>
      checked ? addSpecialtyLevelId(prev, levelId) : removeSpecialtyLevelId(prev, levelId),
    );
  };

  /** Validate, persist drag order if changed, then PATCH. */
  const handleSubmit = async () => {
    const errs = validateSpecialtyForm(formData);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    if (orderedSpecialtyIds) {
      await reorderSpecialties(orderedSpecialtyIds);
    }
    updateMutation.mutate(buildUpdateSpecialtyDto(formData, levelIds));
  };

  return (
    <SlidePanelForm
      formType="edit"
      title="Modifier la spécialité"
      subtitle="Modifiez les informations de la spécialité"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Enregistrer"
      isSubmitting={updateMutation.isPending}
      isValid={isSpecialtyFormValid(formData)}
      isLoading={isLoading || (!!specialty && Object.keys(formData).length === 0)}
    >
      <div className="form-section">
        <h4 className="form-section-title">
          <FileText className="w-4 h-4" />
          Informations générales
        </h4>

        <div className="grid grid-cols-1 gap-4">
          <Input
            label="Libellé"
            value={formData.label ?? ''}
            onChange={handleLabelChange}
            placeholder="Ex: Mathématiques"
            maxLength={255}
            required
            helperText="Nom complet de la spécialité"
            error={errors.label}
            state={errors.label ? 'error' : 'default'}
          />

          <Input
            label="Code"
            value={formData.code ?? ''}
            onChange={handleCodeChange}
            placeholder="Ex: mathematiques"
            maxLength={100}
            required
            helperText="Code unique identifiant la spécialité"
            error={errors.code}
            state={errors.code ? 'error' : 'default'}
          />

          <Checkbox
            label="Spécialité active"
            checked={formData.isActive ?? true}
            onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, isActive: checked }))}
            helperText="Une spécialité inactive n'apparaît pas dans les formulaires"
          />
        </div>
      </div>

      {allSpecialties.length > 1 && (
        <div className="form-section">
          <h4 className="form-section-title">
            <ListOrdered className="w-4 h-4" />
            Ordre dans la liste
          </h4>
          <p className="text-xs text-muted-foreground mb-3">
            Glissez-déposez pour définir l'ordre d'affichage
          </p>
          <SpecialtySortableList
            allSpecialties={allSpecialties}
            currentSpecialtyId={specialtyId}
            onOrderChange={setOrderedSpecialtyIds}
          />
        </div>
      )}

      {highSchoolLevels.length > 0 && (
        <div className="form-section">
          <h4 className="form-section-title">
            <GraduationCap className="w-4 h-4" />
            Niveaux lycée associés
          </h4>
          <div className="grid grid-cols-2 gap-3">
            {highSchoolLevels.map((level) => (
              <Checkbox
                key={level.id}
                id={`level-${level.id}`}
                label={formatSpecialtyLevelLabel(level)}
                checked={levelIds.includes(level.id)}
                onCheckedChange={(checked) => handleToggleLevel(level.id, checked)}
              />
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Sélectionnez les niveaux lycée concernés par cette spécialité (optionnel)
          </p>
        </div>
      )}
    </SlidePanelForm>
  );
};
